import type { WebsiteThemeId } from "@gigflow/shared";

export type ThemeTokens = {
  id: WebsiteThemeId;
  label: string;
  description: string;
  fontDisplay: string;
  fontBody: string;
  radius: string;
  heroOverlay: string;
  surface: string;
  accent: string;
  accentSoft: string;
  text: string;
  muted: string;
  buttonRadius: string;
  density: "comfortable" | "compact" | "spacious";
};

export const THEME_TOKENS: Record<WebsiteThemeId, ThemeTokens> = {
  CLEAN_MODERN: {
    id: "CLEAN_MODERN",
    label: "Clean Modern",
    description: "Airy teal surfaces with soft geometry and clear hierarchy.",
    fontDisplay: '"Fraunces", Georgia, serif',
    fontBody: '"Source Sans 3", system-ui, sans-serif',
    radius: "18px",
    heroOverlay:
      "linear-gradient(135deg, rgba(15,118,110,0.88), rgba(19,78,74,0.72))",
    surface: "rgba(255,255,255,0.92)",
    accent: "#0F766E",
    accentSoft: "#CCFBF1",
    text: "#0F1F1C",
    muted: "#4B635E",
    buttonRadius: "999px",
    density: "comfortable",
  },
  PREMIUM: {
    id: "PREMIUM",
    label: "Premium",
    description:
      "Deep forest panels with gold-edged accents and generous space.",
    fontDisplay: '"Fraunces", Georgia, serif',
    fontBody: '"Source Sans 3", system-ui, sans-serif',
    radius: "12px",
    heroOverlay:
      "linear-gradient(160deg, rgba(6,48,42,0.94), rgba(15,118,110,0.78))",
    surface: "rgba(247,250,249,0.96)",
    accent: "#14532D",
    accentSoft: "#DCFCE7",
    text: "#052E1C",
    muted: "#3F5E52",
    buttonRadius: "10px",
    density: "spacious",
  },
  MINIMAL: {
    id: "MINIMAL",
    label: "Minimal",
    description:
      "Quiet typography, thin rules, and restrained teal highlights.",
    fontDisplay: '"Source Sans 3", system-ui, sans-serif',
    fontBody: '"Source Sans 3", system-ui, sans-serif',
    radius: "6px",
    heroOverlay:
      "linear-gradient(180deg, rgba(240,253,250,0.95), rgba(204,251,241,0.65))",
    surface: "#FFFFFF",
    accent: "#0D9488",
    accentSoft: "#F0FDFA",
    text: "#134E4A",
    muted: "#5B726C",
    buttonRadius: "4px",
    density: "compact",
  },
  BOLD: {
    id: "BOLD",
    label: "Bold",
    description: "High-contrast forest blocks with oversized headlines.",
    fontDisplay: '"Fraunces", Georgia, serif',
    fontBody: '"Source Sans 3", system-ui, sans-serif',
    radius: "0px",
    heroOverlay:
      "linear-gradient(120deg, #064E3B 0%, #0F766E 55%, #115E59 100%)",
    surface: "#ECFDF5",
    accent: "#047857",
    accentSoft: "#A7F3D0",
    text: "#022C22",
    muted: "#065F46",
    buttonRadius: "0px",
    density: "comfortable",
  },
};

export function themeCssVars(
  themeId: WebsiteThemeId,
  primary?: string | null,
  secondary?: string | null,
): Record<string, string> {
  const theme = THEME_TOKENS[themeId] ?? THEME_TOKENS.CLEAN_MODERN;
  return {
    "--site-font-display": theme.fontDisplay,
    "--site-font-body": theme.fontBody,
    "--site-radius": theme.radius,
    "--site-hero-overlay": theme.heroOverlay,
    "--site-surface": theme.surface,
    "--site-accent": primary || theme.accent,
    "--site-accent-soft": theme.accentSoft,
    "--site-secondary": secondary || theme.accent,
    "--site-text": theme.text,
    "--site-muted": theme.muted,
    "--site-button-radius": theme.buttonRadius,
  };
}
